// §18 F5 — "OPFS evicted between sessions".
//
// The hard part is not the message, it is knowing. A guest's settings live in OPFS too (§14), so
// an eviction takes the very thing that would have remembered we were here — after it, the origin
// is indistinguishable from a first visit, and telling a first-time visitor their storage was
// cleared is worse than saying nothing.
//
// So the marker lives in **`localStorage`**, which is evicted under a different policy: the two are
// separate buckets, and a browser reclaiming origin-private files leaves `localStorage` alone far
// more often than not.
//
// The marker means **"this browser held files"**, not "this browser was visited" — a distinction
// that cost a test to find. Planted on arrival it is true of every second page load, so an ordinary
// returning visitor who has never created anything gets told their storage was cleared. Marker
// present *and* filesystem empty is the only combination that describes an eviction.
//
// It is a heuristic and it is stated as one (D-181): the failure mode is a *missed* notice, never a
// false one — no files, no marker, no message.
export const EVICTION_MARKER_KEY = 'hangar.storage';
const MARKER_VALUE = '1';

export interface EvictionScope {
  getItem(key: string): string | null;
  setItem(key: string, value: string): void;
}

/**
 * Record that this browser **held files** — not merely that it was visited.
 *
 * The distinction is the whole heuristic. "We were here" is true of every second page load, so a
 * marker planted on arrival makes the notice fire for an ordinary returning visitor who has simply
 * never created anything. "We had files, and now there are none" is the only combination that
 * actually describes an eviction.
 */
export function markStorageSeen(scope: EvictionScope | undefined, hadFiles: boolean): void {
  if (!hadFiles) return;
  try {
    scope?.setItem(EVICTION_MARKER_KEY, MARKER_VALUE);
  } catch {
    // Private-mode browsers throw on write. A marker we cannot keep is a notice we cannot show,
    // which is the safe direction.
  }
}

/**
 * Were we here before, and is the filesystem now empty?
 *
 * `hadFiles` is what the caller measured — whether the user's own space holds anything at all.
 */
export function wasStorageCleared(scope: EvictionScope | undefined, hadFiles: boolean): boolean {
  if (hadFiles) return false;
  try {
    return scope?.getItem(EVICTION_MARKER_KEY) === MARKER_VALUE;
  } catch {
    return false;
  }
}
